import { formatUtcLocal } from "../../../utils/dateFormat";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Stack,
  Typography,
  Button,
  Box,
  Alert,
  Chip,
} from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridValueFormatterParams,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { Helmet } from "react-helmet-async";
import { useGetImportJobQuery } from "../../../services/API/dealerImportApiSlice";
import { API_GET_DEALER_IMPORT_ERRORS } from "../../../services/ApiConstants";
import { JobRow } from "../../../types/dealerImportTypes";

const statusColor = (s: string) => {
  switch (s) {
    case "Created":
      return "success";
    case "Updated":
      return "info";
    case "Unchanged":
      return "default";
    case "Failed":
      return "error";
    case "Running":
      return "warning";
    default:
      return "default";
  }
};

export default function JobDetail() {
  const { jobId = "" } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { data, isFetching, refetch } = useGetImportJobQuery(jobId, {
    skip: !jobId,
  });

  if (!data) return <Box sx={{ p: 3 }}>Loading…</Box>;
  const s = data.summary;

  const cols: GridColDef<JobRow>[] = [
    { field: "sourceRowNumber", headerName: "Row", flex: 0.3, type: "number" },
    {
      field: "isOwner",
      headerName: "Type",
      flex: 0.5,
      valueFormatter: (params: GridValueFormatterParams) =>
        params.value ? "Owner" : "Location",
    },
    { field: "groupId", headerName: "Group", flex: 0.4 },
    { field: "dmsDealerId", headerName: "DmsDealerId", flex: 0.6 },
    { field: "dmsLocationId", headerName: "DmsLocationId", flex: 0.7 },
    {
      field: "status",
      headerName: "Result",
      flex: 0.6,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          size="small"
          label={params.value}
          color={statusColor(params.value as string) as any}
        />
      ),
    },
    {
      field: "everestDealerId",
      headerName: "DealerId",
      flex: 0.7,
      valueFormatter: (params: GridValueFormatterParams) =>
        params.value != null ? String(params.value) : "",
    },
    { field: "error", headerName: "Error", flex: 2 },
  ];

  // REACT_APP_BASE_URL already ends with a trailing slash and
  // API_GET_DEALER_IMPORT_ERRORS is a controller-relative path with no leading
  // slash, so a plain concatenation produces the correct full URL. A
  // same-origin anchor sends the HttpOnly auth cookie automatically.
  const errorsHref = `${process.env.REACT_APP_BASE_URL}${API_GET_DEALER_IMPORT_ERRORS(
    jobId
  )}`;

  return (
    <>
      <Helmet>
        <title>Import job {jobId.slice(0, 8)}</title>
      </Helmet>
      <Stack spacing={3} sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" gutterBottom style={{ fontWeight: "bold" }}>Import job {jobId.slice(0, 8)}</Typography>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => navigate("/dashboard/dealer-import")}>Back to jobs</Button>
            <Button onClick={() => refetch()}>Refresh</Button>
            {s.failedRows > 0 && (
              <Button
                variant="outlined"
                color="error"
                href={errorsHref}
                target="_blank"
              >
                Download errors CSV
              </Button>
            )}
          </Stack>
        </Stack>

        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Chip
              label={`Status: ${s.status}`}
              color={statusColor(s.status) as any}
            />
            <Typography>File: {s.sourceFileName}</Typography>
            <Typography>By: {s.uploadedBy}</Typography>
            <Typography>Total: {s.totalRows}</Typography>
            <Typography>Created: {s.createdRows}</Typography>
            <Typography>Updated: {s.updatedRows}</Typography>
            <Typography>Unchanged: {s.unchangedRows}</Typography>
            <Typography>Failed: {s.failedRows}</Typography>
            <Typography>
              Completed:{" "}
              {formatUtcLocal(s.completedAt) || "—"}
            </Typography>
          </Stack>
          {s.jobError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {s.jobError}
            </Alert>
          )}
        </Paper>

        <Paper sx={{ p: 2, height: 700 }}>
          <Box sx={{ height: 620 }}>
            <DataGridPro
              rows={data.rows}
              columns={cols}
              getRowId={(r) => r.rowId}
              loading={isFetching}
              disableSelectionOnClick
            />
          </Box>
        </Paper>
      </Stack>
    </>
  );
}
